import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CryptoJS from 'crypto-js';
import SftGlobalData from './library/globalData';
import Sidebar from './components/Sidebar';
import AuthScreen from './screens/AuthScreen';
import WelcomeScreen from './screens/WelcomeScreen';
import DocumentScreen from './screens/DocumentScreen';
import SearchScreen from './screens/SearchScreen';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function AppContent({ onLogin, auth, singleDocMode = false }) {
    const { lang, docId } = useParams();
    const { i18n } = useTranslation();
    const navigate = useNavigate();

    const [menuOpen, setMenuOpen] = useState(window.innerWidth >= 1200 && !singleDocMode);
    const [searchValue, setSearchValue] = useState('');

    console.log('AppContent render:', { lang, docId, singleDocMode });

    // Handle language changes
    useEffect(() => {
        if (lang && SftGlobalData.languages.includes(lang)) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setMenuOpen(window.innerWidth >= 1200);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSearch = useCallback((value) => {
        setSearchValue(value);
        if (value.trim()) {
            navigate(`/${lang || 'nl'}/search/${encodeURIComponent(value)}`);
        }
    }, [navigate, lang]);

    const currentLang = lang || SftGlobalData.defaultLanguage;

    return (
        <div className="app">
            <Sidebar
                isOpen={menuOpen}
                onToggle={() => setMenuOpen(!menuOpen)}
                language={currentLang}
                isLoggedIn={auth.isLoggedIn}
                searchValue={searchValue}
                onSearch={handleSearch}
                singleDocMode={singleDocMode}
            />
            <main className={`main-content ${menuOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                {singleDocMode && docId ? (
                    <DocumentScreen
                        isLoggedIn={auth.isLoggedIn}
                        language={currentLang}
                        docIdOverride={docId}
                    />
                ) : (
                    <Routes>
                        <Route
                            path="/"
                            element={<WelcomeScreen isLoggedIn={auth.isLoggedIn} />}
                        />
                        <Route
                            path="/document/:docId/*"
                            element={
                                <DocumentScreen
                                    isLoggedIn={auth.isLoggedIn}
                                    language={currentLang}
                                />
                            }
                        />
                        <Route
                            path="/search"
                            element={
                                <SearchScreen
                                    isLoggedIn={auth.isLoggedIn}
                                    language={currentLang}
                                    searchValue={searchValue}
                                />
                            }
                        />
                        <Route
                            path="/search/:query"
                            element={
                                <SearchScreen
                                    isLoggedIn={auth.isLoggedIn}
                                    language={currentLang}
                                    searchValue={searchValue}
                                />
                            }
                        />
                    </Routes>
                )}
            </main>
        </div>
    );
}

function App() {
    const [auth, setAuth] = useState({
        isLoggedIn: false,
        userId: null,
        status: null,
        group: null
    });

    // Check for existing session on mount
    useEffect(() => {
        const getCookie = (name) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        };

        const cookie = getCookie(SftGlobalData.cookieName);
        if (cookie) {
            try {
                const decrypted = CryptoJS.AES.decrypt(cookie, SftGlobalData.encryptionKey);
                const data = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
                if (data.isLoggedIn || data.login_active) {
                    setAuth({
                        isLoggedIn: true,
                        userId: data.userId || data.sft_user,
                        status: data.status || data.sft_status,
                        group: data.group || data.sft_group
                    });
                }
            } catch (e) {
                // Invalid cookie, ignore
            }
        }
    }, []);

    const handleLogin = useCallback((userData) => {
        const authData = {
            isLoggedIn: true,
            userId: userData.sft_user_id || userData.sft_user,
            status: userData.sft_status,
            group: userData.sft_group
        };
        setAuth(authData);

        // Encrypt and store in cookie
        const encrypted = CryptoJS.AES.encrypt(
            JSON.stringify(authData),
            SftGlobalData.encryptionKey
        ).toString();

        const date = new Date();
        date.setTime(date.getTime() + (SftGlobalData.cookieExpireMinutes * 60 * 1000));
        document.cookie = `${SftGlobalData.cookieName}=${encrypted};expires=${date.toUTCString()};path=/`;
    }, []);

    return (
        <ErrorBoundary>
            <Routes>
                {/* Auth routes without language prefix */}
                <Route path="/auth/:key" element={<AuthScreen onLogin={handleLogin} />} />
                <Route path="/mysft/:sftKey" element={<AuthScreen onLogin={handleLogin} />} />
                <Route path="/red/:redirect" element={<AuthScreen onLogin={handleLogin} singleDocMode={true} />} />

                {/* Auth routes with language prefix */}
                <Route path="/:lang/auth/:key" element={<AuthScreen onLogin={handleLogin} />} />
                <Route path="/:lang/mysft/:sftKey" element={<AuthScreen onLogin={handleLogin} />} />
                <Route path="/:lang/red/:redirect" element={<AuthScreen onLogin={handleLogin} singleDocMode={true} />} />

                {/* Single document view (sidebar with language only) */}
                <Route path="/:lang/view/:docId/*" element={<AppContent onLogin={setAuth} auth={auth} singleDocMode={true} />} />

                {/* Main app routes */}
                <Route path="/:lang/*" element={<AppContent onLogin={setAuth} auth={auth} />} />
                <Route path="*" element={<AppContent onLogin={setAuth} auth={auth} />} />
            </Routes>
        </ErrorBoundary>
    );
}

export default App;
